/**
 * Row options — declaration + composition (rows-side, no DOM).
 *
 * Each row variant declares what it can configure via
 * `ModelRow.optionContributions()`. A visible row's full options
 * surface is COMPOSED by walking its hydrated subtree and merging the
 * contributions of every descendant — so an alias/layout that subsumes
 * a tree of implementation rows exposes their options without each
 * plugin hardcoding them.
 *
 * The composer is pure logic and DOM-free: contributions declare a
 * typed `ControlSpec` (kind + params), not a widget factory, and a
 * `Target` (the canonical write path), not a closure. `@tento-lona/
 * sheets-ui` maps `control.kind -> widget` and resolves `target ->
 * write`; both the legend popover and the property-list drawer render
 * from the same `Model`.
 *
 * Design: lona-sdk-2.0.project.docs/content/row-options-composition.md.
 */

import type { Option } from "../core/option.ts";
import { Json, type JsonObject, jsonOf } from "../core/json.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import type { SheetModelReads } from "../sheet/rows-sheet-view.ts";
import { type ModelRowReads } from "./model-row-reads.ts";
import { liveRowOf } from "./model-row.ts";
import type { ModelSheet } from "../sheet/rows-sheet.ts";
import { sheetInternals, sheetStores } from "../sheet/sheet-stores.ts";

import { AliasRow, aliasWriterOf } from "./variants/alias-row.ts";
import { FormulaDataSource, formulaWriterOf } from "./variants/formula-data-source.ts";

/**
 * The options a row contributes to its menu and the property list, and where
 * each one sits.
 */
export namespace ModelRowOptions {
  /**
   * Where a contribution sits in the composed surface.
   * - `primary`  — inline on the main surface (changed per session).
   * - `section`  — grouped under a named, navigable section (drill-in).
   * - `advanced` — only in the full drawer (property-list.full).
   *
   * Mirrors `property-list.default` (primary + section) vs `.full`
   * (everything) so the popover and the drawer render the same model at
   * two depths.
   */
  export type Tier = "primary" | "section" | "advanced";

  /** DOM-free control descriptor. `@tento/lona/sheets-ui` maps `kind -> widget`. */
  export type ControlSpec =
    // `invert: true` when the toggle displays the inverse of the stored
    // attr (e.g. "Monthly view" shown = `!disableMonthlyView`).
    | { readonly kind: "toggle"; readonly invert?: boolean }
    | { readonly kind: "color" }
    | { readonly kind: "timezone" }
    | { readonly kind: "text" }
    | { readonly kind: "code"; readonly language: "plain" | "lisp" }
    | { readonly kind: "action" }
    | {
        readonly kind: "range";
        readonly min: number;
        readonly max: number;
        readonly step?: number;
      }
    | {
        /** Two-handle hour window (startHr/endHr) — one control, two values. */
        readonly kind: "time-range";
        readonly min: number;
        readonly max: number;
      }
    | {
        readonly kind: "select";
        readonly options: readonly {
          readonly value: string;
          readonly label: string;
        }[];
      };

  /**
   * The canonical write path for a contribution. Pure data — the
   * owning row's id plus the field to write. The UI (or a rows-side
   * writer) resolves the row and performs the variant-specific write
   * (the alias prop write / a renderer's attributes / a layout row's
   * metadata / source metadata).
   */
  export type Target =
    // `AliasRow.setProp` — a template-bound alias prop (mirrors into
    // `attributes.props` + round-trips through `AliasTemplate.bindings`).
    | { readonly kind: "alias-prop"; readonly rowId: RowLocalId; readonly prop: string }
    // `patchAttributes` on an alias's top-level attribute bag. Calendar's
    // `startHr`/`type`/`timezone`/… live here today, not in `props`.
    | { readonly kind: "alias-attr"; readonly rowId: RowLocalId; readonly attr: string }
    // Two attrs written by one two-handle control (e.g. `startHr`/`endHr`).
    | {
        readonly kind: "alias-attr-range";
        readonly rowId: RowLocalId;
        readonly startAttr: string;
        readonly endAttr: string;
      }
    // Like `alias-attr`, but the write lands in the alias's per-child OVERRIDE
    // slot `attributes.children[preset]` (not the top level), so a virtual
    // child's config (calendar Hours/type/timezone, persisted on its host
    // alias) round-trips without touching the alias's top-level bag. Read
    // CASCADES slot → top-level (read-compat for legacy top-level config).
    | {
        readonly kind: "alias-child-attr";
        readonly rowId: RowLocalId;
        readonly preset: string;
        readonly attr: string;
      }
    | {
        readonly kind: "alias-child-attr-range";
        readonly rowId: RowLocalId;
        readonly preset: string;
        readonly startAttr: string;
        readonly endAttr: string;
      }
    | { readonly kind: "layout-attr"; readonly rowId: RowLocalId; readonly attr: string }
    | { readonly kind: "renderer-attr"; readonly rowId: RowLocalId; readonly attr: string }
    // `patchAttributes` on the SOURCE row's bag. A renderer's display
    // options (number `format`/`type`, checkmark `checkColor`, `unit`)
    // live on the source so they round-trip through serialize/hydrate.
    | { readonly kind: "source-attr"; readonly rowId: RowLocalId; readonly attr: string }
    | {
        readonly kind: "source-metadata";
        readonly rowId: RowLocalId;
        readonly path: readonly string[];
      }
    | {
        /**
         * App-owned preference (e.g. weather "set as my default"), not
         * row/alias/source state. Flagged so the UI routes it to the
         * app and audits can find non-row writes.
         */
        readonly kind: "app-pref";
        readonly pref: string;
      };

  /**
   * How the host should re-render after this option is written, so the
   * change becomes visible. Declared by the contributing row (decentralized,
   * like `tier`); one app dispatcher maps it to the host's `rebind` calls.
   * Omitted ⇒ `"cells"`.
   * - `"cells"`     — re-bind the visible cells; a pure content change
   *   (number format, checkmark color) needs nothing more.
   * - `"layout"`    — recompute row height/layout, THEN re-bind; the change
   *   alters the body's geometry (calendar hour window, list↔grid,
   *   monthly-view, timezone).
   * - `"structure"` — rebuild the flattened tree + layout + structure; the
   *   change adds/removes a projected row (the all-day strip).
   * - `"rejoin"`    — re-join the rows-side tree into the client tree + refresh
   *   the flattened projection, THEN re-bind visible cells in place. Same as
   *   `"structure"` MINUS the full `bindSheet` teardown. Use when an EXISTING
   *   row's join address moved but the row SET is unchanged (re-keying a weather
   *   alias child to a new `:~weather:{lid}`): the new address must re-join to
   *   bind, but recycling + repainting every cell (what `"structure"` does) is a
   *   whole-sheet flash the change never warranted.
   */
  export type ReRender = "cells" | "layout" | "structure" | "rejoin";

  /** One configurable option a row declares. */
  export interface Contribution {
    /**
     * Stable dedupe key, `"<role>:<semantic>"` (e.g.
     * `"calendar:hour-range"`). Two nodes describing the SAME product
     * option MUST share a key so the composer renders one control.
     */
    readonly key: string;
    readonly label: string;
    readonly tier: Tier;
    /** Rank within the tier; higher sorts first. */
    readonly priority: number;
    /** Section name; set when `tier !== "primary"`. */
    readonly section: Option<string>;
    readonly target: Target;
    readonly control: ControlSpec;
    /** Re-render the host needs after a write. Omitted ⇒ `"cells"`. */
    readonly reRender?: ReRender;
  }

  /** A value a control produces — the payload written to a `Target`. */
  export type Value =
    | boolean
    | number
    | string
    | null
    | { readonly start: number; readonly end: number };

  /** A named group of `section`-tier contributions. */
  export interface Section {
    readonly name: string;
    readonly items: readonly Contribution[];
  }

  /**
   * The composed options surface for one visible row. Both surfaces
   * render from this single model:
   * - legend popover: `primary` inline + `sections` (drill-in) + a
   *   "More options →" affordance that opens the drawer.
   * - drawer (property-list.full): `primary` + `overflow` + `sections`
   *   + `advanced`.
   */
  export interface Model {
    readonly primary: readonly Contribution[];
    readonly sections: readonly Section[];
    readonly advanced: readonly Contribution[];
    /** `primary` contributions beyond the budget — shown only in the drawer. */
    readonly overflow: readonly Contribution[];
  }

  /** Tree access for the composer — mirrors `PropertyListProjection.Deps`. */
  export interface Deps<Row> {
    readonly contributionsOf: (row: Row) => readonly Contribution[];
    readonly childrenOf: (row: Row) => readonly Row[];
  }

  export interface ComposeOptions {
    /** Max inline `primary` controls before overflow. Default 4. */
    readonly primaryBudget?: number;
  }

  /**
   * Compose the options surface for `root` by walking its subtree,
   * collecting every node's contributions, de-duping by `key`, and
   * grouping by tier. Generic over `Row` so it unit-tests with fakes;
   * `composeSheetRow` wires the `ModelRow` version.
   */
  export function compose<Row>(
    root: Row,
    deps: Deps<Row>,
    options: Option<ComposeOptions>,
  ): Model {
    const budget = options?.primaryBudget ?? DEFAULT_PRIMARY_BUDGET;

    const collected: Contribution[] = [];
    collect(root, deps, collected);
    const deduped = dedupe(collected);

    const primaryAll = deduped
      .filter((c) => c.tier === "primary")
      .sort(byPriority);
    const advanced = deduped
      .filter((c) => c.tier === "advanced")
      .sort(byPriority);
    const sectionItems = deduped.filter((c) => c.tier === "section");

    return {
      primary: primaryAll.slice(0, budget),
      overflow: primaryAll.slice(budget),
      sections: groupSections(sectionItems),
      advanced,
    };
  }

  /** `compose` wired to a rows-side row's tree (a live row or its reads face). */
  export function composeSheetRow(
    root: ModelRowReads,
    options: Option<ComposeOptions>,
  ): Model {
    return compose(root, SHEET_ROW_DEPS, options);
  }

  /**
   * Whether a composed `Model` surfaces any control. The reveal path uses this
   * to decide composed-vs-legacy: an empty model (the row declares no
   * contributions, and none of its subtree does) means fall back to the row's
   * plugin `makeRowOptions` factory.
   */
  export function isModelNonEmpty(model: Model): boolean {
    return (
      model.primary.length > 0 ||
      model.sections.length > 0 ||
      model.advanced.length > 0 ||
      model.overflow.length > 0
    );
  }

  /**
   * Read the current value at a `Target` so the UI can initialize its
   * control. The inverse of the option write (`RowEnvelopeDrive.writeTarget`).
   * Only the formula source-metadata path is rows-side readable; other `source-metadata`/`app-pref` targets return
   * `null` so their owners route them deliberately.
   */
  export function read(sheet: SheetModelReads, target: Target): Value {
    switch (target.kind) {
      case "alias-prop": {
        // `sheet` hands out reads faces: narrow the live row, not the face.
        const read = sheet.peekRow(target.rowId);
        const row = read == null ? null : liveRowOf(read);
        if (!(row instanceof AliasRow)) return null;
        return toValue(jsonOf(row.instance().props[target.prop]));
      }
      case "renderer-attr": {
        // Dual-read: the renderer's OWN bag wins once materialized + customized,
        // else fall back to the source (parent) bag where the value lives until
        // first customization — so the control initializes to the right value
        // even while the default renderer is still virtual.
        const own =
          sheet.rowMeta(target.rowId).attributes?.[target.attr];
        if (own !== null && own !== undefined) return toValue(own);
        const parent = sheet.peekRow(target.rowId)?.parent;
        const sourceAttrs = parent
          ? sheet.rowMeta(parent.id).attributes
          : undefined;
        return toValue(sourceAttrs?.[target.attr] ?? null);
      }
      case "alias-attr":
      case "layout-attr":
      case "source-attr": {
        const attrs = sheet.rowMeta(target.rowId).attributes;
        return toValue(attrs?.[target.attr] ?? null);
      }
      case "alias-attr-range": {
        const attrs = sheet.rowMeta(target.rowId).attributes;
        const start = toValue(attrs?.[target.startAttr] ?? null);
        const end = toValue(attrs?.[target.endAttr] ?? null);
        return {
          start: typeof start === "number" ? start : 0,
          end: typeof end === "number" ? end : 24,
        };
      }
      case "alias-child-attr": {
        const attrs = sheet.rowMeta(target.rowId).attributes;
        const slot = childSlot(attrs ?? null, target.preset);
        return toValue((slot?.[target.attr] ?? attrs?.[target.attr]) ?? null);
      }
      case "alias-child-attr-range": {
        const attrs = sheet.rowMeta(target.rowId).attributes;
        const slot = childSlot(attrs ?? null, target.preset);
        const start = toValue((slot?.[target.startAttr] ?? attrs?.[target.startAttr]) ?? null);
        const end = toValue((slot?.[target.endAttr] ?? attrs?.[target.endAttr]) ?? null);
        return {
          start: typeof start === "number" ? start : 0,
          end: typeof end === "number" ? end : 24,
        };
      }
      case "source-metadata": {
        if (isFormulaMetadataTarget(target)) {
          const read = sheet.peekRow(target.rowId);
          const row = read == null ? null : liveRowOf(read);
          return row instanceof FormulaDataSource ? row.expression : null;
        }
        return null;
      }
      case "app-pref":
        return null;
    }
  }

  /**
   * The `attributes` patch a value implies for an attr-kind `Target` —
   * for UI callers that persist through their own row-update path (e.g.
   * `row.update({ attributes: { ...current, ...patch } })`) instead of
   * the store-only `write`. `null` for non-attr targets (`alias-prop`,
   * `source-metadata`, `app-pref`).
   */
  export function attrsFor(
    target: Target,
    value: Value,
  ): Option<JsonObject> {
    switch (target.kind) {
      case "alias-attr":
      case "layout-attr":
      case "renderer-attr":
      case "source-attr":
        return { [target.attr]: value };
      case "alias-attr-range":
        if (!isRange(value)) return null;
        return { [target.startAttr]: value.start, [target.endAttr]: value.end };
      // Child-override targets persist through the envelope drive
      // (`sdkRow.envelopes.apply`), which reads the post-write `children` map
      // and persists it with the row's attributes — a flat
      // patch here would wrongly land on the alias TOP level (split-brain), so
      // these intentionally yield no flat patch.
      case "alias-child-attr":
      case "alias-child-attr-range":
        return null;
      default:
        return null;
    }
  }
}

/** The per-child override slot `attributes.children[preset]`, or null. */
function childSlot(
  attrs: Option<JsonObject>,
  preset: string,
): Option<JsonObject> {
  return Json.child(Json.child(attrs, "children"), preset);
}

/**
 * Persist a value through its `Target`'s canonical write path — the option
 * write of an envelope (`RowEnvelopeDrive.writeTarget`, which the SDK row's
 * `row.envelopes.apply(msg, actor)` drives); not a member of
 * `ModelRowOptions`, whose published surface is the read vocabulary:
 * `alias-prop` → `AliasRow.setProp`; every `*-attr` (and the paired
 * range) → `rowMetadata.patchAttributes` on the owning row. The
 * generic attribute writes are store-only (no backend), so the same
 * key the contribution reads from is the key it writes to.
 *
 * `source-metadata` is intentionally narrow: formula edits route through the
 * FormulaDataSource mutation seam, while lookupKey and future metadata paths
 * stay explicit errors until they have an owner.
 */
export async function writeOptionTarget(
  sheet: ModelSheet,
  target: ModelRowOptions.Target,
  value: ModelRowOptions.Value,
): Promise<void> {
  switch (target.kind) {
    case "alias-prop": {
      const row = sheet.peekRow(target.rowId);
      if (!(row instanceof AliasRow)) {
        throw new Error(
          `RowEnvelopeDrive.writeTarget: alias-prop target ${target.rowId.rawId} ` +
            `is not an AliasRow`,
        );
      }
      await aliasWriterOf(row).setProp(target.prop, value);
      return;
    }
    case "renderer-attr":
    case "alias-attr":
    case "layout-attr":
    case "source-attr": {
      // Every scalar `*-attr` routes through the one optimistic-local-first
      // seam: patch + paint, then best-effort persist. `setDisplayAttr`
      // detects a renderer target (a renderer child of a source) and applies
      // copy-on-write — materializing the source's still-virtual default
      // renderer to a real, persisted row before the attr lands on the
      // RENDERER's own bag — so renderer display options round-trip through
      // serialize → hydrate. The other kinds patch the named row's own bag.
      if (isRange(value)) {
        throw new Error(
          `RowEnvelopeDrive.writeTarget: ${target.kind} target expects a scalar, ` +
            `got a range`,
        );
      }
      await sheetInternals(sheet).setDisplayAttr(target.rowId, { [target.attr]: value });
      return;
    }
    case "alias-attr-range": {
      if (!isRange(value)) {
        throw new Error(
          `RowEnvelopeDrive.writeTarget: alias-attr-range target expects ` +
            `{start,end}, got ${typeof value}`,
        );
      }
      await sheetInternals(sheet).setDisplayAttr(target.rowId, {
        [target.startAttr]: value.start,
        [target.endAttr]: value.end,
      });
      return;
    }
    case "alias-child-attr": {
      if (isRange(value)) {
        throw new Error(
          `RowEnvelopeDrive.writeTarget: alias-child-attr target expects a scalar, ` +
            `got a range`,
        );
      }
      await sheetInternals(sheet).setChildDisplayAttrs(target.rowId, target.preset, {
        [target.attr]: value,
      });
      return;
    }
    case "alias-child-attr-range": {
      if (!isRange(value)) {
        throw new Error(
          `RowEnvelopeDrive.writeTarget: alias-child-attr-range target expects ` +
            `{start,end}, got ${typeof value}`,
        );
      }
      await sheetInternals(sheet).setChildDisplayAttrs(target.rowId, target.preset, {
        [target.startAttr]: value.start,
        [target.endAttr]: value.end,
      });
      return;
    }
    case "source-metadata": {
      if (isFormulaMetadataTarget(target)) {
        if (typeof value !== "string") {
          throw new Error(
            `RowEnvelopeDrive.writeTarget: formula target expects a string, ` +
              `got ${typeof value}`,
          );
        }
        const row = sheet.peekRow(target.rowId);
        if (!(row instanceof FormulaDataSource)) {
          throw new Error(
            `RowEnvelopeDrive.writeTarget: formula target ${target.rowId.rawId} ` +
              `is not a FormulaDataSource`,
          );
        }
        await formulaWriterOf(row).setExpression(value);
        return;
      }
      throw new Error(
        `RowEnvelopeDrive.writeTarget: source-metadata writes are not routed here ` +
          `(${target.path.join(".")})`,
      );
    }
    case "app-pref":
      throw new Error(
        `RowEnvelopeDrive.writeTarget: app-pref "${target.pref}" must be handled ` +
          `by the app layer, not a row write`,
      );
  }
}

/** Narrow an attribute-bag value to a scalar `Value` (anything else is `null`). */
function toValue(raw: Option<Json>): ModelRowOptions.Value {
  if (raw === null || raw === undefined) return null;
  if (
    typeof raw === "boolean" || typeof raw === "number" ||
    typeof raw === "string"
  ) {
    return raw;
  }
  return null;
}

const DEFAULT_PRIMARY_BUDGET = 4;

/**
 * Tie-break order when two contributions share a `key`: the canonical
 * owner is the most product-facing write path. Alias props win (the
 * alias surfaces the option on the visible row) over layout/renderer
 * attrs, which win over source metadata, which wins over app prefs.
 *
 * TODO: when `AliasTemplate.bindings` declares an explicit canonical
 * owner for a key, prefer that over this default ordering.
 */
const TARGET_RANK: Record<ModelRowOptions.Target["kind"], number> = {
  "alias-prop": 0,
  "alias-attr": 1,
  "alias-attr-range": 1,
  "alias-child-attr": 1,
  "alias-child-attr-range": 1,
  "layout-attr": 2,
  "renderer-attr": 3,
  "source-attr": 4,
  "source-metadata": 5,
  "app-pref": 6,
};

const SHEET_ROW_DEPS: ModelRowOptions.Deps<ModelRowReads> = {
  contributionsOf: (row) => row.optionContributions(),
  childrenOf: (row) => row.children,
};

export function isRange(
  value: ModelRowOptions.Value,
): value is { readonly start: number; readonly end: number } {
  return typeof value === "object" && value !== null && "start" in value &&
    "end" in value;
}

function isFormulaMetadataTarget(target: ModelRowOptions.Target): boolean {
  return target.kind === "source-metadata" && target.path.length === 1 &&
    target.path[0] === "formula";
}

function collect<Row>(
  row: Row,
  deps: ModelRowOptions.Deps<Row>,
  out: ModelRowOptions.Contribution[],
): void {
  for (const c of deps.contributionsOf(row)) out.push(c);
  for (const child of deps.childrenOf(row)) collect(child, deps, out);
}

/** Keep one contribution per `key` — the canonical owner (see `beats`). */
function dedupe(
  contributions: readonly ModelRowOptions.Contribution[],
): ModelRowOptions.Contribution[] {
  const winners = new Map<string, ModelRowOptions.Contribution>();
  for (const c of contributions) {
    const prev = winners.get(c.key);
    if (prev === undefined || beats(c, prev)) winners.set(c.key, c);
  }
  return [...winners.values()];
}

function beats(
  candidate: ModelRowOptions.Contribution,
  incumbent: ModelRowOptions.Contribution,
): boolean {
  if (candidate.priority !== incumbent.priority) {
    return candidate.priority > incumbent.priority;
  }
  return TARGET_RANK[candidate.target.kind] < TARGET_RANK[incumbent.target.kind];
}

/** Descending by priority, stable by label. */
function byPriority(
  a: ModelRowOptions.Contribution,
  b: ModelRowOptions.Contribution,
): number {
  if (a.priority !== b.priority) return b.priority - a.priority;
  return a.label.localeCompare(b.label);
}

function groupSections(
  items: readonly ModelRowOptions.Contribution[],
): ModelRowOptions.Section[] {
  const groups = new Map<string, ModelRowOptions.Contribution[]>();
  for (const c of items) {
    const name = c.section ?? "Options";
    const list = groups.get(name);
    if (list) list.push(c);
    else groups.set(name, [c]);
  }

  const sections: ModelRowOptions.Section[] = [];
  for (const [name, list] of groups) {
    sections.push({ name, items: [...list].sort(byPriority) });
  }

  // Most important section first (by its top item), stable by name.
  sections.sort((a, b) => {
    const pa = a.items[0]?.priority ?? 0;
    const pb = b.items[0]?.priority ?? 0;
    if (pa !== pb) return pb - pa;
    return a.name.localeCompare(b.name);
  });
  return sections;
}
